<template>
  <div class="vd-dialog">
    <el-dialog
      :model-value="visible"
      :fullscreen="maximized"
      :close-on-click-modal="closeOnClickOutside"
      destroy-on-close
      @closed="onClose"
    >
      <div v-if="title" class="header" :class="`${type}`">
        <h1>{{ title }}</h1>
        <div v-if="!hideCloseButton" class="close-button" data-cy="dialog-close-button" @click="onClose">
          <vd-custom-icon width="30" box="0 0 30 30" active name="close-icon" />
        </div>
      </div>
      <slot v-else name="header"></slot>
      <div v-loading="loading" class="content">
        <slot />
      </div>
      <div class="footer">
        <slot name="footer" />
      </div>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

/**
 * This structural component represents a modal dialog.
 * It can be used as a component on a page and triggered via a "visible" property
 * The default close button emit an event "close".
 */

export default defineComponent({
  name: "VDDialog",

  props: {
    /**
     * Title of the dialog, if present it will show in a header, with a close box near it, if not a slot "header" is available
     */
    title: {
      type: String,
      required: false,
      default: null,
    },

    /**
     * is the dialog open ?
     */
    visible: {
      type: Boolean,
      required: true,
    },

    /**
     * the width of the dialog
     */
    width: {
      type: String,
      default: "60%",
    },

    /**
     * the type of dialog: info | error
     */
    type: {
      type: String,
      default: "info",
    },

    /**
     * Wether do display the hide button
     */
    hideCloseButton: {
      type: Boolean,
      default: false,
    },

    /**
     * If true, the dialog width/height will be stretched
     */
    maximized: {
      type: Boolean,
      default: false,
    },

    /**
     * Know if the content of the request or body is loading
     */
    loading: {
      type: Boolean,
      default: false,
    },

    /**
     * Allows to close the dialog when clicking on the mask.
     */
    closeOnClickOutside: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["close"],

  setup(_, { emit }) {
    const onClose = () => {
      emit("close");
    };

    return {
      onClose,
    };
  },
});
</script>

<style lang="scss">
.vd-dialog {
  .el-dialog {
    overflow-y: auto;
    background-color: var(--vd-page-background-color);

    .close-button {
      cursor: pointer;
    }

    .header {
      &.info {
        background-color: var(--voodootune-highlight-color);
      }

      &.error {
        background-color: $light-red;
        color: $red-alert;
      }

      display: flex;
      width: 100%;
      justify-content: space-between;
      align-items: center;
      padding: 27px size("ML") 29px size("ML");
      margin-bottom: size("M");

      z-index: $z-dialog-fixed-elements;
    }

    .content {
      overflow: hidden;
      overflow-y: auto;
      box-sizing: border-box;
      padding: size("ML");
      word-break: break-word;

      z-index: $z-dialog-scrollable-elements;
    }

    .footer {
      z-index: $z-dialog-fixed-elements;
      text-align: center;
      padding-bottom: size("ML");
    }

    .el-dialog__header {
      display: none;
    }

    .el-dialog__body {
      padding: 0;
      color: var(--vd-base-primary-text-color);
      background-color: var(--vd-page-background-color);
    }

    &.is-fullscreen {
      --el-dialog-width: 90%;
      top: 50%;
      transform: translateY(-50%);
      min-height: 60vh;
      max-height: 90vh;
      max-width: 1280px;
      overflow-y: scroll;

      .content {
        height: calc(100vh - 100px);
        max-height: calc(82vh - 130px);
      }
    }
  }
}
</style>
